import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useBranches, useCreateBranch, useUpdateBranch, useSetBranchActive } from '../hooks/useBranches';
import { BranchForm, type BranchFormValues } from '../components/BranchForm';
import type { Branch } from '../api/branchesApi';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';

/**
 * Branch / location management (PE-2139).
 *
 * Scoped to the admin's selected merchant. Note branches use an explicit `merchantId`
 * param rather than the x-tenant-id header — see branchesApi.ts.
 */
export function BranchesPage() {
  // In Osprey the selected tenant IS the merchant (same id used for /merchants/:id).
  const merchantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.BRANCH_MANAGE);

  const { data: branches = [], isLoading, error } = useBranches(merchantId);
  const createBranch = useCreateBranch(merchantId);
  const updateBranch = useUpdateBranch(merchantId);
  const setActive = useSetBranchActive(merchantId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (branch: Branch) => {
    setEditing(branch);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const isSaving = createBranch.isPending || updateBranch.isPending;

  const handleSubmit = async (values: BranchFormValues) => {
    if (editing) {
      await updateBranch.mutateAsync({ id: editing.id, data: values });
    } else {
      if (!merchantId) return;
      await createBranch.mutateAsync(values);
    }
    closeModal();
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Branches</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Manage this merchant's locations. Products, booking services and packages are available
            at every branch unless you exclude them.
          </p>
        </div>
        {canManage && merchantId && (
          <Button size="sm" className="h-[40px]" onClick={openCreate}>
            New branch
          </Button>
        )}
      </div>

      {!merchantId ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          Select a merchant to manage their branches.
        </div>
      ) : isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading branches…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">Failed to load branches. Please try again.</div>
      ) : branches.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No branches yet.
          {canManage && ' Click "New branch" to create one.'}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Address</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                {canManage && (
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {branches.map((branch) => (
                <tr key={branch.id} className="hover:bg-muted transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {branch.name}
                    {branch.description && (
                      <span className="block text-xs text-muted-foreground font-normal">
                        {branch.description}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{branch.address || '—'}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                        branch.isActive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {branch.isActive ? 'Active' : 'Removed'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 space-x-3">
                      <button onClick={() => openEdit(branch)} className="text-xs text-blue-600 hover:underline">
                        Edit
                      </button>
                      {/*
                        There is no DELETE route — removal deactivates the branch so historical
                        references survive, which is why this is reversible rather than destructive.
                      */}
                      <button
                        onClick={() => setActive.mutate({ id: branch.id, isActive: !branch.isActive })}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {branch.isActive ? 'Remove' : 'Re-activate'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              {editing ? 'Edit Branch' : 'New Branch'}
            </h2>
            <button
              onClick={closeModal}
              className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none"
            >
              &times;
            </button>
          </div>
          <BranchForm initial={editing} onSubmit={handleSubmit} onCancel={closeModal} isSaving={isSaving} />
        </div>
      </Modal>
    </div>
  );
}
